import { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Calendar, User, Castle, RefreshCw } from "lucide-react";
import { useReservations, Reservation } from "@/hooks/useReservations";

const statusConfig: Record<string, { label: string; color: string; badgeVariant: "default" | "secondary" | "destructive" | "outline" }> = {
  en_attente: { label: "En attente", color: "bg-amber-100 text-amber-800", badgeVariant: "outline" },
  loue: { label: "Loué", color: "bg-emerald-100 text-emerald-800", badgeVariant: "default" },
  annule: { label: "Annulé", color: "bg-red-100 text-red-800", badgeVariant: "destructive" },
};

export const ReservationsManager = () => {
  const { reservations, loading, updateStatus, deleteReservation, fetchReservations } = useReservations();
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const filtered = filterStatus === "all" 
    ? reservations 
    : reservations.filter(r => r.status === filterStatus);

  const counts = {
    all: reservations.length,
    en_attente: reservations.filter(r => r.status === 'en_attente').length,
    loue: reservations.filter(r => r.status === 'loue').length,
    annule: reservations.filter(r => r.status === 'annule').length,
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Chargement des réservations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-display font-semibold">Gestion des Réservations</h3>
          <p className="text-sm text-muted-foreground">{reservations.length} réservation(s) au total</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchReservations()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: "Toutes" },
          { key: "en_attente", label: "En attente" },
          { key: "loue", label: "Loué" },
          { key: "annule", label: "Annulé" },
        ].map(({ key, label }) => (
          <Button
            key={key}
            variant={filterStatus === key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(key)}
            className="relative"
          >
            {label}
            <Badge variant="secondary" className="ml-2 text-xs">
              {counts[key as keyof typeof counts]}
            </Badge>
          </Button>
        ))}
      </div>

      {/* Reservations List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Aucune réservation pour ce filtre.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map((reservation) => (
            <ReservationCard
              key={reservation.id}
              reservation={reservation}
              onStatusChange={updateStatus}
              onDelete={deleteReservation}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ReservationCardProps {
  reservation: Reservation;
  onStatusChange: (id: string, status: string) => void;
  onDelete: (id: string) => void;
}

const ReservationCard = ({ reservation, onStatusChange, onDelete }: ReservationCardProps) => {
  const config = statusConfig[reservation.status] || statusConfig.en_attente;

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "dd MMM yyyy", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow border-l-4" style={{
      borderLeftColor: reservation.status === 'loue' ? 'hsl(var(--primary))' : 
                       reservation.status === 'annule' ? 'hsl(var(--destructive))' : 
                       'hsl(var(--accent))'
    }}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Info */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Client */}
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{reservation.client_name}</p>
                <p className="text-xs text-muted-foreground truncate">{reservation.client_email}</p>
                <p className="text-xs text-muted-foreground">{reservation.client_phone}</p>
              </div>
            </div>

            {/* Product */}
            <div className="flex items-center gap-2">
              <Castle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">{reservation.product_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{reservation.product_type}</p>
              </div>
            </div>

            {/* Dates */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-sm">{formatDate(reservation.date_debut)}</p>
                <p className="text-xs text-muted-foreground">→ {formatDate(reservation.date_fin)}</p>
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex items-center">
              <Badge className={config.color}>
                {config.label}
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={reservation.status}
              onValueChange={(value) => onStatusChange(reservation.id, value)}
            >
              <SelectTrigger className="w-[140px] bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border shadow-lg z-50">
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="loue">Loué</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="destructive"
              size="icon"
              onClick={() => {
                if (confirm(`Supprimer la réservation de ${reservation.client_name} ?`)) {
                  onDelete(reservation.id);
                }
              }}
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Message */}
        {reservation.message && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted-foreground italic">"{reservation.message}"</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
